import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, 
  Mail, 
  Phone, 
  Lock,
  Shield,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Eye,
  EyeOff,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';
import InternationalPhoneInput from '@/components/common/InternationalPhoneInput';

interface InvitationData {
  invitation_id: number;
  tailor_id: number;
  tailor_name: string;
  tailor_email: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  expires_at: string;
  is_valid: boolean;
}

const CustomerInvitation: React.FC = () => {
  const { token: invitationToken } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Registration form state
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (invitationToken) {
      validateInvitation();
    }
  }, [invitationToken]);

  const validateInvitation = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/customers/invite/validate/${invitationToken}`);
      
      if (response.ok) {
        const data = await response.json();
        setInvitationData(data);
        
        // Pre-fill form with invitation data
        if (data.customer_name) {
          setFormData(prev => ({ ...prev, full_name: data.customer_name }));
        }
        if (data.customer_phone) {
          setFormData(prev => ({ ...prev, phone: data.customer_phone || '' }));
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Invalid or expired invitation. Please request a new one from your tailor.');
      }
    } catch (_err) {
      console.error('Validation error:', err);
      setError('Failed to validate invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!formData.full_name.trim()) {
      setError('Full name is required');
      return;
    }
    
    if (!invitationData) {
      setError('Invitation data not loaded');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/customers/invite/register/${invitationToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: formData.password,
          full_name: formData.full_name.trim(),
          phone: formData.phone.trim() || null,
          email: invitationData.customer_email || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Auto-login with the returned token
        if (data.access_token) {
          await login(data.user.email, formData.password);
        }
        
        setSuccess(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to complete registration. Please try again.');
      }
    } catch (_err) {
      console.error('Registration error:', err);
      setError('Network error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Masterji Logo Component
  const MasterjiLogo = ({ size = 32, className = "" }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M8 12C8 10.5 9 9 10.5 9C12 9 13 10.5 13 12C13 13.5 12 15 10.5 15C9 15 8 13.5 8 12Z" fill="currentColor"/>
      <path d="M19 12C19 10.5 20 9 21.5 9C23 9 24 10.5 24 12C24 13.5 23 15 21.5 15C20 15 19 13.5 19 12Z" fill="currentColor"/>
      <path d="M10.5 12L21.5 12" stroke="currentColor" strokeWidth="1"/>
      <path d="M16 6C16 4.5 17 3 18.5 3C20 3 21 4.5 21 6C21 7.5 20 9 18.5 9C17 9 16 7.5 16 6Z" fill="currentColor"/>
      <path d="M16 6L16 12" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6 18L26 18" stroke="currentColor" strokeWidth="2"/>
      <path d="M8 20L24 20" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M4 8C4 6 6 4 8 4L24 4C26 4 28 6 28 8L28 10C28 12 26 14 24 14L8 14C6 14 4 12 4 10L4 8Z" fill="#FFBE00" stroke="currentColor" strokeWidth="0.5"/>
      <path d="M6 6L6 8M8 6L8 8M10 6L10 8M12 6L12 8M14 6L14 8M16 6L16 8M18 6L18 8M20 6L20 8M22 6L22 8M24 6L24 8M26 6L26 8" stroke="currentColor" strokeWidth="0.3"/>
    </svg>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <MasterjiLogo size={48} className="text-blue-600 mx-auto mb-4" />
          <div className="text-gray-600">Validating invitation...</div>
        </div>
      </div>
    );
  }

  if (error && !invitationData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <MasterjiLogo size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invitation Not Valid</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <MasterjiLogo size={48} className="text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Registration Complete!</h1>
          <p className="text-gray-600 mb-6">
            You have successfully registered and are now connected with {invitationData?.tailor_name}
          </p>
          <div className="text-sm text-gray-500">Redirecting to your dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <MasterjiLogo size={32} className="text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Master Ji</h1>
                <p className="text-sm text-gray-500">Premium Tailoring Platform</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {invitationData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Invitation Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <MasterjiLogo size={64} className="text-blue-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Registration</h1>
              <p className="text-lg text-gray-600">
                <span className="font-semibold text-blue-600">{invitationData.tailor_name}</span> has invited you to join MasterJi
              </p>
            </div>

            {/* Registration Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email (read-only if from invitation) */}
                {invitationData.customer_email && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={invitationData.customer_email}
                      disabled
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    />
                    <p className="mt-1 text-xs text-gray-500">This email was provided by your tailor</p>
                  </div>
                )}

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone Number {invitationData.customer_phone ? '' : '*'}
                  </label>
                  <InternationalPhoneInput
                    value={formData.phone}
                    onChange={(phone, isValid) => {
                      setFormData(prev => ({ ...prev, phone }));
                    }}
                    placeholder="Enter phone number"
                    defaultCountry="in"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline mr-1" />
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      minLength={8}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                      placeholder="Create a password (min. 8 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters long</p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline mr-1" />
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      minLength={8}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">What happens next?</p>
                      <ul className="space-y-1 text-xs">
                        <li>• You'll be automatically connected with {invitationData.tailor_name}</li>
                        <li>• You can start placing orders and managing your measurements</li>
                        <li>• Your data is encrypted and protected with enterprise-grade security</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Complete Registration
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default CustomerInvitation;

